import { packageServer, packageResponseText } from "../i18n/packageManagementPresentation";
import { packageText as pm, usePackageManagementLanguage, packageFailure, packageFailureText, type PackageFailure } from "../i18n/packageManagementPresentation";
/**
 * Hook for triggering and streaming a per-app package update.
 *
 * Calls POST /api/packages/:name/update then connects to
 * SSE at /api/packages/jobs/:jobId/stream
 * for live output. Falls back to polling /status if SSE disconnects.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { getCsrfToken } from "../lib/csrf";

export type AppUpdateJobStatus = "idle" | "queued" | "running" | "succeeded" | "failed" | "aborted";

type TriggerUpdateOptions = {
  /** Target version for reinstall (e.g., "0.16.5"). */
  version?: string;
  /** Endpoint action — "reinstall" for version selection, "update" for standard. */
  action?: "update" | "reinstall";
  /**
   * Explicit Plex channel override. When absent, the backend autodetects.
   * "free"     → job started immediately (no credentials required)
   * "plexpass" → credentials required; backend returns credentialsRequired: true
   */
  plexChannel?: "free" | "plexpass";
  /**
   * Plex credentials for the PlexPass credential gate.
   * Sent alongside plexChannel: "plexpass" to create the job.
   */
  plexCredentials?: {
    plexEmail: string;
    plexPassword: string;
    plexOtp: string;
  };
};

/**
 * Discriminated union returned by triggerUpdate.
 *
 *  ok: true                   — job started; transition to job-stream UI
 *  ok: false, credentialsRequired  — PlexPass detected; open PlexCredentialModal
 *  ok: false, needsChannelSelection — autodetect unknown; open PlexChannelSelectorModal
 *  ok: false, message         — hard error; show error
 */
type TriggerUpdateResult =
  | { ok: true; message: string }
  | { ok: false; message: string }
  | { ok: false; credentialsRequired: true; detectedChannel?: "plexpass" }
  | { ok: false; needsChannelSelection: true; detectedChannel: "unknown" };

type UseAppUpdateReturn = {
  triggerUpdate: (packageName: string, options?: TriggerUpdateOptions) => Promise<TriggerUpdateResult>;
  /** Disconnect SSE/polling and mark as aborted without clearing log output. */
  abort: () => void;
  isUpdating: boolean;
  jobId: string | null;
  jobStatus: AppUpdateJobStatus;
  jobProgress: number;
  jobStep: string | null;
  jobError: string | null;
  jobLogTail: string[];
  reset: () => void;
};

const POLL_INTERVAL_MS = 3000;

export const useAppUpdate = (): UseAppUpdateReturn => {
  usePackageManagementLanguage();
  const [jobId, setJobId] = useState<string | null>(null);
  const [jobStatus, setJobStatus] = useState<AppUpdateJobStatus>("idle");
  const [jobProgress, setJobProgress] = useState(0);
  const [jobStep, setJobStep] = useState<string | null>(null);
  const [jobError, setJobError] = useState<PackageFailure | null>(null);
  const [jobLogTail, setJobLogTail] = useState<string[]>([]);

  const eventSourceRef = useRef<EventSource | null>(null);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const mountedRef = useRef(true);
  const statusRef = useRef<AppUpdateJobStatus>("idle");
  statusRef.current = jobStatus;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      eventSourceRef.current?.close();
      eventSourceRef.current = null;
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, []);

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  const disconnectSSE = useCallback(() => {
    eventSourceRef.current?.close();
    eventSourceRef.current = null;
    stopPolling();
  }, [stopPolling]);

  /** Polling fallback when SSE disconnects mid-job. */
  const startPollingFallback = useCallback((targetJobId: string) => {
    if (pollTimerRef.current) return;

    const poll = async () => {
      const s = statusRef.current;
      if (s === "succeeded" || s === "failed") {
        stopPolling();
        return;
      }
      try {
        const res = await fetch(`/api/packages/jobs/${targetJobId}/status`, {
          credentials: "include",
          cache: "no-store",
        });
        if (!res.ok || !mountedRef.current) return;
        const data = await res.json() as { ok: boolean; data?: { job?: Record<string, unknown> } };
        if (!data.ok || !data.data?.job) return;

        const job = data.data.job;
        const status = job.status as AppUpdateJobStatus;
        setJobStatus(status);

        const lastLines = job.lastLines as string[] | undefined;
        if (lastLines && lastLines.length > 0) {
          setJobLogTail((prev) => {
            const existing = new Set(prev.slice(-lastLines.length));
            const newLines = lastLines.filter((line) => !existing.has(line));
            return newLines.length > 0 ? [...prev, ...newLines] : prev;
          });
        }

        if (status === "succeeded" || status === "failed") {
          stopPolling();
        }
      } catch {
        // Non-fatal, retry next interval
      }
    };

    void poll();
    pollTimerRef.current = setInterval(() => void poll(), POLL_INTERVAL_MS);
  }, [stopPolling]);

  /** Connect to SSE stream for live output. */
  const connectSSE = useCallback((targetJobId: string) => {
    disconnectSSE();

    const es = new EventSource(
      `/api/packages/jobs/${targetJobId}/stream`,
      { withCredentials: true },
    );
    eventSourceRef.current = es;

    es.addEventListener("init", (event: MessageEvent) => {
      if (!mountedRef.current) return;
      try {
        const parsed = JSON.parse(event.data as string) as { status?: string };
        if (parsed.status === "running") {
          setJobStatus("running");
        }
      } catch { /* ignore */ }
    });

    es.addEventListener("line", (event: MessageEvent) => {
      if (!mountedRef.current) return;
      try {
        const parsed = JSON.parse(event.data as string) as { text: string };
        setJobLogTail((prev) => [...prev, parsed.text]);
      } catch { /* ignore */ }
    });

    es.addEventListener("status", (event: MessageEvent) => {
      if (!mountedRef.current) return;
      try {
        const parsed = JSON.parse(event.data as string) as {
          status: string;
          exitCode?: number | null;
          durationMs?: number | null;
        };
        const status = parsed.status as AppUpdateJobStatus;
        if (status === "succeeded" || status === "failed" || status === "aborted") {
          es.close();
          eventSourceRef.current = null;
          setJobStatus(status);
          if (status === "failed") {
            setJobError(parsed.exitCode != null ? { key: "updateExit", values: { code: parsed.exitCode } } : { key: "updateFailed" });
          }
        } else {
          setJobStatus(status);
        }
      } catch { /* ignore */ }
    });

    es.onerror = () => {
      es.close();
      eventSourceRef.current = null;
      const s = statusRef.current;
      if (s !== "succeeded" && s !== "failed") {
        startPollingFallback(targetJobId);
      }
    };
  }, [disconnectSSE, startPollingFallback]);

  const triggerUpdate = useCallback(
    async (
      packageName: string,
      options?: TriggerUpdateOptions,
    ): Promise<TriggerUpdateResult> => {
      setJobStatus("queued");
      setJobProgress(0);
      setJobStep(null);
      setJobError(null);
      setJobLogTail([]);

      try {
        const csrfToken = getCsrfToken();
        const headers: Record<string, string> = { "Content-Type": "application/json" };
        if (csrfToken) headers["x-csrf-token"] = csrfToken;

        const action = options?.action ?? "update";
        const endpoint = `/api/packages/${encodeURIComponent(packageName)}/${action}`;

        // Build request body — always JSON so CSRF header is valid
        type UpdateBody = {
          version?: string;
          plexChannel?: "free" | "plexpass";
          plexEmail?: string;
          plexPassword?: string;
          plexOtp?: string;
        };
        const body: UpdateBody = {};
        if (options?.version) body.version = options.version;
        if (options?.plexChannel) body.plexChannel = options.plexChannel;
        if (options?.plexCredentials) {
          body.plexEmail = options.plexCredentials.plexEmail;
          body.plexPassword = options.plexCredentials.plexPassword;
          body.plexOtp = options.plexCredentials.plexOtp;
        }

        const res = await fetch(endpoint, {
          method: "POST",
          credentials: "include",
          headers,
          body: JSON.stringify(body),
        });

        const data = (await res.json()) as {
          ok: boolean;
          data?: {
            jobId?: string;
            status?: string;
            message?: string;
            credentialsRequired?: boolean;
            needsChannelSelection?: boolean;
            detectedChannel?: "plexpass" | "unknown";
          };
          message?: string;
        };

        // Autodetect: PlexPass detected — collect credentials before creating job.
        // Reset job state so the credential modal opens cleanly.
        if (res.ok && data.ok && data.data?.credentialsRequired) {
          setJobStatus("idle");
          return { ok: false, credentialsRequired: true, detectedChannel: data.data.detectedChannel as "plexpass" | undefined };
        }

        // Autodetect: channel unknown — let the user pick Free or PlexPass.
        if (res.ok && data.ok && data.data?.needsChannelSelection) {
          setJobStatus("idle");
          return { ok: false, needsChannelSelection: true, detectedChannel: "unknown" };
        }

        if (!res.ok || !data.ok) {
          const msg =
            packageServer(data, data.message ?? data.data?.message ?? { key: "updateStartFailed" as const });
          setJobStatus("failed");
          setJobError(msg);
          return { ok: false, message: packageFailureText(msg) ?? pm("updateStartFailed") };
        }

        const newJobId = data.data?.jobId ?? null;
        setJobId(newJobId);

        if (newJobId) {
          connectSSE(newJobId);
        }

        return {
          ok: true,
          message: packageResponseText(data, data.data?.message ?? pm("updateStarted")),
        };
      } catch (err) {
        const msg = packageFailure(err, "networkError");
        setJobStatus("failed");
        setJobError(msg);
        return { ok: false, message: packageFailureText(msg) ?? pm("updateStartFailed") };
      }
    },
    [connectSSE]
  );

  const reset = useCallback(() => {
    disconnectSSE();
    setJobId(null);
    setJobStatus("idle");
    setJobProgress(0);
    setJobStep(null);
    setJobError(null);
    setJobLogTail([]);
  }, [disconnectSSE]);

  /** Stop the SSE/polling connection and set status to "aborted" without clearing log output. */
  const abort = useCallback(() => {
    disconnectSSE();
    setJobStatus("aborted");
  }, [disconnectSSE]);

  const isUpdating = jobStatus === "queued" || jobStatus === "running";

  return {
    triggerUpdate,
    abort,
    isUpdating,
    jobId,
    jobStatus,
    jobProgress,
    jobStep,
    jobError: packageFailureText(jobError),
    jobLogTail,
    reset,
  };
};
